const namedImportRegex =
  // 1: maybe name for default (imports only):  import foo from "package"
  // 2: maybe * part:                           import * as myModule from "package"
  //    note that these can be combined:        import foo, * as myModule from "package"
  // 3: maybe names:                            import { foo, bar } from "package"
  //    (can be combined with 1 but not 2)
  // Matches of \s in the middle of the string are non-greedy to reduce potential backtracking.
  // The { names } match is very crude (all possible characters in any order) to improve performance,
  // since we know the input is syntactically valid and we'll actually match the names later.
  //                   ( 1    )           ( 2                     )          ( 3       )       from   path
  /^\s*[ie][mx]port\s*?([\w$]+)?\s*?,?\s*?(\*(?:\s*?as\s+?[\w$]+)?)?\s*?(?:\{([\w$\s,]*)\})?\s*from\s*["']/;

/**
 * Parses a JS import/export statement (from a path) into its component parts.
 * Does NOT handle typescript (`import/export type`), imports not from paths, exports without paths,
 * or anything that's not a syntactically valid import/export statement.
 *
 * The statement is expected to be copied verbatim from a source file, so there's a slight chance it
 * could contain comments embedded in the middle. This will try to remove the comments, but
 * accuracy is not guaranteed (especially for contrived scenarios like imports inside comments).
 *
 * @param importLine - The import string to parse.
 * @returns A string array with the named imports, `'default'` and/or `'*'`.
 * This will return the original names, not the imported names.
 */
export function parseNamedImports(importLine: string): string[] {
  let matches = importLine.match(namedImportRegex);

  if (!matches) {
    if (/^import\s*?["'][^"']*["']/.test(importLine)) {
      // This is a side effect import, so return empty array.
      return [];
    }

    // Maybe there are some embedded comments? Try removing them.
    importLine = importLine
      // Start by finding and removing the path to reduce chance of accidents.
      .replace(/\bfrom\s*(["'])[^"']*["'].*/, 'from "removed"')
      // Remove single-line and multi-line comments. This could behave badly in contrived
      // nesting scenarios, but should be reliable for more common cases.
      .replaceAll(/\/\/.*/g, '')
      .replaceAll(/\/\*[\s\S]*?\*\//g, '');

    // Try again
    matches = importLine.match(namedImportRegex);
    if (!matches) {
      return [];
    }
  }

  const names: string[] = [];
  if (matches[1]) {
    // Default export
    names.push('default');
  }

  if (matches[3]) {
    // Imported or exported names
    // Clean out the whitespace, and get the original names not the aliases
    const namedImports = matches[3].matchAll(/([\w$]+)(?:\s+as\s+[\w$]+,?)?/g);
    for (const namedImport of namedImports) {
      names.push(namedImport[1]);
    }
  }

  if (matches[2]) {
    // All names
    names.push('*');
  }

  return names;
}
